
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { usePageTracking } from "@/hooks/useAnalytics";
import { Suspense, lazy } from "react";
import { initSentry, Sentry } from "@/utils/sentry";
import { SEOOptimizer } from "@/components/SEOOptimizer";
import { EnhancedLoadingFallback } from "@/components/ui/enhanced-loading-fallback";
import { UserFeedbackChat } from "@/components/UserFeedbackChat";
import { CookieConsent } from "@/components/CookieConsent";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { CartProvider } from "@/contexts/CartContext";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import UnsubscribePage from "./pages/UnsubscribePage";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { Footer } from "@/components/Footer";

// Initialize Sentry for error tracking
initSentry();

// Component to redirect old blog URLs to articles
const BlogRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/articles/${slug}`} replace />;
};

// Lazy load pages for better performance
const RaceResults = lazy(() => import("./pages/RaceResults"));
const RaceTips = lazy(() => import("./pages/RaceTips"));
const OddsAnalysis = lazy(() => import("./pages/OddsAnalysis"));
const HorseDetails = lazy(() => import("./pages/HorseDetails"));
const JockeyDetails = lazy(() => import("./pages/JockeyDetails"));
const Jockeys = lazy(() => import("./pages/Jockeys"));
const Horses = lazy(() => import("./pages/Horses"));
const TipAnalysis = lazy(() => import("./pages/TipAnalysis"));
const RaceAnalysis = lazy(() => import("./pages/RaceAnalysis"));
const RaceAnalysisOverview = lazy(() => import("./pages/RaceAnalysisOverview"));
const Fantasy = lazy(() => import("./pages/Fantasy"));
const FantasyLeaderboard = lazy(() => import("./pages/FantasyLeaderboard"));
const Chov = lazy(() => import("./pages/Chov"));
const UserTipDetails = lazy(() => import("./pages/UserTipDetails"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));

// Lazy load admin pages
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminAccessManagement = lazy(() => import("./pages/AdminAccessManagement"));
const AdminOdds = lazy(() => import("./pages/AdminOdds"));
const AdminLineups = lazy(() => import("./pages/AdminLineups"));
const AdminRaceResults = lazy(() => import("./pages/AdminRaceResults"));
const AdminManualResults = lazy(() => import("./pages/AdminManualResults"));
const AdminHorseIntros = lazy(() => import("./pages/AdminHorseIntros"));
const AdminDataManagement = lazy(() => import("./pages/AdminDataManagement"));
const AdminMailing = lazy(() => import("./pages/AdminMailing"));
const AdminSyndicateEmails = lazy(() => import("./pages/AdminSyndicateEmails"));
const AdminHorseDuplicates = lazy(() => import("./pages/AdminHorseDuplicates"));
const AdminJockeyDuplicates = lazy(() => import("./pages/AdminJockeyDuplicates"));
const AdminRaceCreator = lazy(() => import("./pages/AdminRaceCreator"));
const AdminRaceEditor = lazy(() => import("./pages/AdminRaceEditor"));
const AdminRacePhotos = lazy(() => import("./pages/AdminRacePhotos"));
const TimeDistance = lazy(() => import("./pages/TimeDistance"));
const HelpGuide = lazy(() => import("./pages/HelpGuide"));
const AdminFantasySettings = lazy(() => import("./pages/AdminFantasySettings"));
const FantasyFeedback = lazy(() => import("./pages/FantasyFeedback"));
const AdminResetLeague = lazy(() => import("./pages/AdminResetLeague"));
const AdminPolls = lazy(() => import("./pages/AdminPolls"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminBreedingInterests = lazy(() => import("./pages/AdminBreedingInterests"));
const AdminSyndicates = lazy(() => import("./pages/AdminSyndicates"));
const AdminSyndicateMembers = lazy(() => import("./pages/AdminSyndicateMembers"));
const AdminSyndicateAdminsPage = lazy(() => import("./pages/AdminSyndicateAdminsPage"));
const AdminSyndicateEventsPage = lazy(() => import("./pages/AdminSyndicateEvents"));
const AdminPrizeMoney = lazy(() => import("./pages/AdminPrizeMoney"));
const AdminGiftCodes = lazy(() => import("./pages/AdminGiftCodes"));
const AdminSyndicateAnalytics = lazy(() => import("./pages/AdminSyndicateAnalytics"));
const Articles = lazy(() => import("./pages/Articles"));
const ArticlePost = lazy(() => import("./pages/ArticlePost"));
const FantasyLanding = lazy(() => import("./pages/FantasyLanding"));
const StatsLanding = lazy(() => import("./pages/StatsLanding"));
const OddsLanding = lazy(() => import("./pages/OddsLanding"));
const Syndicates = lazy(() => import("./pages/Syndicates"));
const SyndicateLanding = lazy(() => import("./pages/SyndicateLanding"));
const GiftCodeActivation = lazy(() => import("./pages/GiftCodeActivation"));
const MySyndicate = lazy(() => import("./pages/MySyndicate"));
const SyndicateAdminPanel = lazy(() => import("./pages/SyndicateAdminPanel"));
const CertificatePreview = lazy(() => import("./pages/CertificatePreview"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Complaints = lazy(() => import("./pages/Complaints"));
const Withdrawal = lazy(() => import("./pages/Withdrawal"));
const SyndicateFAQ = lazy(() => import("./pages/SyndicateFAQ"));
const Merch = lazy(() => import("./pages/Merch"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const MerchCheckout = lazy(() => import("./pages/MerchCheckout"));
const ShopSuccess = lazy(() => import("./pages/ShopSuccess"));
const ShopCancelled = lazy(() => import("./pages/ShopCancelled"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const MyCredits = lazy(() => import("./pages/MyCredits"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const SyndicateSuccess = lazy(() => import("./pages/SyndicateSuccess"));
const AdminCredits = lazy(() => import("./pages/AdminCredits"));
const AdminDiscountCodes = lazy(() => import("./pages/AdminDiscountCodes"));
const EbookLibrary = lazy(() => import("./pages/EbookLibrary"));
const Ebook = lazy(() => import("./pages/Ebook"));
const SilkDesigner = lazy(() => import("./pages/SilkDesigner"));
const SyndicatePrintLanding = lazy(() => import("./pages/SyndicatePrintLanding"));
const RacePropositions = lazy(() => import("./pages/RacePropositions"));
const TrainerDashboard = lazy(() => import("./pages/TrainerDashboard"));
const AdminTrainers = lazy(() => import("./pages/AdminTrainers"));
const DostihovaSkola = lazy(() => import("./pages/DostihovaSkola"));
const AdminGuestRsvps = lazy(() => import("./pages/AdminGuestRsvps"));
const AdminAttendance = lazy(() => import("./pages/AdminAttendance"));
const AdminFinancialDashboard = lazy(() => import("./pages/AdminFinancialDashboard"));
const AdminBrevoOutreach = lazy(() => import("./pages/AdminBrevoOutreach"));
const AdminOwnersBallots = lazy(() => import("./pages/AdminOwnersBallots"));
const AdminTranslations = lazy(() => import("./pages/AdminTranslations"));
const EnHome = lazy(() => import("./pages/en/EnHome"));
const EnSyndicates = lazy(() => import("./pages/en/EnSyndicates"));
const EnAbout = lazy(() => import("./pages/en/EnAbout"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes default
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

// Component to handle page tracking
const PageTracker = () => {
  usePageTracking();
  return null;
};

// Component to conditionally render global navigation
const GlobalLayout = () => {
  const location = useLocation();
  const isShopPage = location.pathname.startsWith('/shop') || 
                     location.pathname.startsWith('/moje-objednavky') ||
                     location.pathname.startsWith('/moje-podkovy');
  const isPrintPage = location.pathname === '/syndicates/print';
  
  return (
    <>
      {!isShopPage && !isPrintPage && <HeaderNavigation />}
      {!isPrintPage && <UserFeedbackChat />}
      {!isPrintPage && <CookieConsent />}
    </>
  );
};

// Component to conditionally render footer
const ConditionalFooter = () => {
  const location = useLocation();
  const isShopPage = location.pathname.startsWith('/shop') || 
                     location.pathname.startsWith('/moje-objednavky') ||
                     location.pathname.startsWith('/moje-podkovy');
  const isPrintPage = location.pathname === '/syndicates/print';
  
  return !isShopPage && !isPrintPage ? <Footer /> : null;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6">
              <div className="max-w-md w-full space-y-4 text-center">
                <h1 className="text-2xl font-bold text-destructive">Něco se pokazilo</h1>
                <p className="text-muted-foreground">Omlouváme se, ale došlo k neočekávané chybě.</p>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto text-left max-h-48">
                  {errorMessage}
                </pre>
                <button
                  onClick={resetError}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                  Zkusit znovu
                </button>
              </div>
            </div>
          );
        }}
      >
        <CookieConsentProvider>
        <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <PageTracker />
            <SEOOptimizer />
            <GlobalLayout />
            <Suspense fallback={<EnhancedLoadingFallback showStaticContent={true} />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/tipy-na-dostihy" element={<RaceTips />} />
              <Route path="/horses" element={<Horses />} />
              <Route path="/jockeys" element={<Jockeys />} />
              <Route path="/race-results" element={<RaceResults />} />
              <Route path="/race-results/:slug" element={<RaceResults />} />
              <Route path="/odds-analysis" element={<OddsAnalysis />} />
               <Route path="/tip-analysis" element={<TipAnalysis />} />
                <Route path="/tip-analysis/user/:userId" element={<UserTipDetails />} />
               <Route path="/syndicates" element={<Syndicates />} />
               <Route path="/syndikaty/:slug" element={<SyndicateLanding />} />
               <Route path="/syndikaty/faq" element={<SyndicateFAQ />} />
                 <Route path="/syndicates/:slug" element={<MySyndicate />} />
                 <Route path="/my-syndicate" element={<Navigate to="/syndicates" replace />} />
                 <Route path="/syndicate-admin" element={<SyndicateAdminPanel />} />
                 <Route path="/darkovy-kod" element={<GiftCodeActivation />} />
               <Route path="/chov" element={<Chov />} />
               <Route path="/fantasy" element={<Fantasy />} />
               <Route path="/fantasy/leaderboard" element={<FantasyLeaderboard />} />
               <Route path="/fantasy/feedback" element={<FantasyFeedback />} />
              <Route path="/race-analysis" element={<RaceAnalysisOverview />} />
               <Route path="/race-analysis/:slug" element={<RaceAnalysis />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/articles/:slug" element={<ArticlePost />} />
                {/* Redirect old blog URLs to articles */}
                <Route path="/blog" element={<Navigate to="/articles" replace />} />
                <Route path="/blog/:slug" element={<BlogRedirect />} />
                
               
               {/* Landing pages */}
               <Route path="/fantasy-landing" element={<FantasyLanding />} />
               <Route path="/stats-landing" element={<StatsLanding />} />
               <Route path="/odds-landing" element={<OddsLanding />} />
              
              {/* SEO-friendly URLs for individual entities */}
              <Route path="/horse/:slug" element={<HorseDetails />} />
              <Route path="/jockey/:slug" element={<JockeyDetails />} />
              
              {/* Category and location-based URLs */}
              <Route path="/horses/:category" element={<Horses />} />
              <Route path="/races/:location" element={<RaceResults />} />
              <Route path="/races/:location/:date" element={<RaceResults />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/access-management" element={<AdminAccessManagement />} />
              <Route path="/admin/odds" element={<AdminOdds />} />
              <Route path="/admin/lineups" element={<AdminLineups />} />
              <Route path="/admin/results" element={<AdminRaceResults />} />
              <Route path="/admin/manual-results" element={<AdminManualResults />} />
              <Route path="/admin/data" element={<AdminDataManagement />} />
              <Route path="/admin/horse-intros" element={<AdminHorseIntros />} />
               <Route path="/admin/mailing" element={<AdminMailing />} />
                <Route path="/admin/syndicate-emails" element={<AdminSyndicateEmails />} />
                <Route path="/admin/fantasy-settings" element={<AdminFantasySettings />} />
               <Route path="/admin/polls" element={<AdminPolls />} />
               <Route path="/admin/blog" element={<AdminBlog />} />
               <Route path="/admin/breeding-interests" element={<AdminBreedingInterests />} />
               <Route path="/admin/syndicates" element={<AdminSyndicates />} />
               <Route path="/admin/syndicate-members" element={<AdminSyndicateMembers />} />
               <Route path="/admin/syndicate-admins" element={<AdminSyndicateAdminsPage />} />
               <Route path="/admin/syndicate-events" element={<AdminSyndicateEventsPage />} />
               <Route path="/admin/prize-money" element={<AdminPrizeMoney />} />
               <Route path="/admin/gift-codes" element={<AdminGiftCodes />} />
               <Route path="/admin/syndicate-analytics" element={<AdminSyndicateAnalytics />} />
                <Route path="/admin/certificate-preview" element={<CertificatePreview />} />
                <Route path="/admin/reset-league" element={<AdminResetLeague />} />
              <Route path="/admin-horse-duplicates" element={<AdminHorseDuplicates />} />
              <Route path="/admin-jockey-duplicates" element={<AdminJockeyDuplicates />} />
              <Route path="/admin/race-creator" element={<AdminRaceCreator />} />
              <Route path="/admin/race-editor" element={<AdminRaceEditor />} />
              <Route path="/admin/race-photos" element={<AdminRacePhotos />} />
              <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
              <Route path="/time-distance" element={<TimeDistance />} />
              <Route path="/help" element={<HelpGuide />} />
              <Route path="/user-dashboard" element={<UserDashboard />} />
              
              {/* Legal pages */}
              <Route path="/obchodni-podminky" element={<Terms />} />
              <Route path="/ochrana-osobnich-udaju" element={<Privacy />} />
              <Route path="/reklamacni-rad" element={<Complaints />} />
              <Route path="/odstoupeni-od-smlouvy" element={<Withdrawal />} />
              
              {/* Merch shop */}
              <Route path="/shop" element={<Merch />} />
              <Route path="/shop/product/:slug" element={<ProductDetail />} />
              <Route path="/shop/checkout" element={<MerchCheckout />} />
              <Route path="/shop/success" element={<ShopSuccess />} />
              <Route path="/shop/cancelled" element={<ShopCancelled />} />
              <Route path="/moje-objednavky" element={<MyOrders />} />
              <Route path="/moje-podkovy" element={<MyCredits />} />
              
              {/* Admin credits */}
              <Route path="/admin/credits" element={<AdminCredits />} />
              <Route path="/admin/discount-codes" element={<AdminDiscountCodes />} />
               <Route path="/admin/guest-rsvps" element={<AdminGuestRsvps />} />
               <Route path="/admin/attendance" element={<AdminAttendance />} />
               <Route path="/admin/financial" element={<AdminFinancialDashboard />} />
               <Route path="/admin/brevo-outreach" element={<AdminBrevoOutreach />} />
               <Route path="/admin/owners-ballots" element={<AdminOwnersBallots />} />
               <Route path="/admin/translations" element={<AdminTranslations />} />

               {/* English marketing pages */}
               <Route path="/en" element={<EnHome />} />
               <Route path="/en/syndicates" element={<EnSyndicates />} />
               <Route path="/en/about" element={<EnAbout />} />
              
              {/* Syndicate success */}
              <Route path="/syndicate-success" element={<SyndicateSuccess />} />
              {/* Redirect old bundle-success to syndicate-success */}
              <Route path="/bundle-success" element={<Navigate to="/syndicate-success" replace />} />
              
              {/* About */}
              <Route path="/o-nas" element={<AboutUs />} />
              
              {/* Ebook library and reader for syndicate members */}
              <Route path="/ebook" element={<EbookLibrary />} />
              <Route path="/ebook/:issueId" element={<Ebook />} />
              
              {/* Silk designer */}
              <Route path="/dres-mixer" element={<SilkDesigner />} />
              
              {/* Print landing page for syndicates */}
              <Route path="/syndicates/print" element={<SyndicatePrintLanding />} />
              
              {/* Race propositions */}
              <Route path="/propozice" element={<RacePropositions />} />
              
              {/* Trainer section */}
              <Route path="/treneri" element={<TrainerDashboard />} />
              <Route path="/admin/trainers" element={<AdminTrainers />} />
              <Route path="/dostihovaskola" element={<DostihovaSkola />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <ConditionalFooter />
          </BrowserRouter>
        </TooltipProvider>
        </CartProvider>
        </CookieConsentProvider>
    </Sentry.ErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
